import React, {useCallback, useMemo} from 'react';
import {Route, Routes, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
    buildHotspotExplorerIndex,
    getHotspotExplorerPeriodFilter
} from "./helpers";
import {endPerfTimer, startPerfTimer} from "../../helpers/perf";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../../helpers/storedBirdData";
import type {HotspotExplorerProps} from "./types";
import {AllHotspotsView} from "./all-hotspots";
import {CountryHotspotsView} from "./country";
import {CountyHotspotsView} from "./county";
import {HotspotDetailView} from "./hotspot-detail";
import {StateHotspotsView} from "./state";

export default function HotspotExplorer(props: HotspotExplorerProps) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const periodFilter = useMemo(() => getHotspotExplorerPeriodFilter(searchParams), [searchParams]);
    const yearOptions = useMemo(
        () => props.years
            .map(year => Number(year))
            .filter((year): year is number => Number.isInteger(year) && year >= PRE_EBIRD_LIST_BUILDING_YEAR && year <= 2100)
            .sort((a, b) => b - a),
        [props.years]
    );
    const selectedYear = periodFilter.year ? String(periodFilter.year) : 'life';
    const selectedMonth = periodFilter.month ? String(periodFilter.month) : 'all';
    const setSelectedPeriod = useCallback((nextYear: string, nextMonth: string) => {
        const nextParams = new URLSearchParams(searchParams);

        nextParams.delete('page');

        if (nextYear === 'life') {
            nextParams.delete('year');
            if (nextMonth === 'all') {
                nextParams.delete('month');
            } else {
                nextParams.set('month', nextMonth);
            }
        } else {
            nextParams.set('year', nextYear);
            if (nextMonth === 'all' || Number(nextYear) === PRE_EBIRD_LIST_BUILDING_YEAR) {
                nextParams.delete('month');
            } else {
                nextParams.set('month', nextMonth);
            }
        }

        const nextSearch = nextParams.toString();
        navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, {replace: true});
    }, [location.pathname, navigate, searchParams]);
    const hotspotExplorerIndex = useMemo(() => {
        const timer = startPerfTimer('hotspotExplorer:buildIndex');
        const result = buildHotspotExplorerIndex(props.observationsByLocation, periodFilter);
        endPerfTimer(timer);
        return result;
    }, [periodFilter, props.observationsByLocation]);
    const routeProps = useMemo(() => ({
        ...props,
        hotspotExplorerIndex,
        periodFilter,
        selectedYear,
        selectedMonth,
        yearOptions,
        onChangePeriod: setSelectedPeriod
    }), [hotspotExplorerIndex, periodFilter, props, selectedMonth, selectedYear, yearOptions, setSelectedPeriod]);
    const routeTree = useMemo(() => {
        return (
            <Routes>
                <Route path="/hotspot-explorer" element={<AllHotspotsView {...routeProps} />} />
                <Route path="/hotspot-explorer/list/:countryCode" element={<CountryHotspotsView {...routeProps} />} />
                <Route path="/hotspot-explorer/list/:countryCode/:stateProvince" element={<StateHotspotsView {...routeProps} />} />
                <Route path="/hotspot-explorer/list/:countryCode/:stateProvince/:county" element={<CountyHotspotsView {...routeProps} />} />
                <Route path="/hotspot-explorer/hotspot/:locationId" element={<HotspotDetailView {...routeProps} />} />
            </Routes>
        );
    }, [routeProps]);

    return routeTree;
}
